import { createSelector } from "reselect"
import { filter, stubFalse, mapValues, pick, get, noop } from "lodash"
import { getIsOpPro } from "modules/account-root/selectors"
import { getPortfolioBetaInstrument } from "modules/portfolio-root/selectors/beta-instrument"
import { Creators } from "../model"
import { computeColumns, makeComputeAssetAnalyticsColumns, betaColumnFilter } from "helpers/assets"
import { getRawSort, getSelectedSubView, getModelId, getDisableColumnClick, getSelectedMetricType, getSelectedPerformanceMetricType } from "."
import { getFactorsColumns } from "./factorColumns"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { getSelectedViewIsContentSet } from "selectors/contentSets"
import { getSelectedView } from "selectors/assets"
import { coalesce } from "helpers/selectors"
import { getSelectedModelName } from "modules/portfolio-root/selectors/portfolios"
import { formatBetaInstrumentTooltip } from "helpers/contributors"
import { getCategories } from "selectors/factors"

const getRawGMV = state => get(state, "global.data.assetAnalyticsComposition.data.model.simulation.composition.0.gmv")
const getRawGroupedGMV = state => get(state, "global.data.assetAnalyticsGroupedComposition.data.model.simulation.composition.0.gmv")
const getRawReferenceEquity = state => get(state, "global.data.assetAnalyticsComposition.data.model.simulation.composition.0.referenceEquity")
const getRawGroupedReferenceEquity = state => get(state, "global.data.assetAnalyticsGroupedComposition.data.model.simulation.composition.0.referenceEquity")

const getGMV = createSelector(
  [getRawGMV, getRawGroupedGMV],
  coalesce,
)

const getReferenceEquity = createSelector(
  [getRawReferenceEquity, getRawGroupedReferenceEquity],
  coalesce,
)

// noop replaces the getIndexLeverage that the selector expects. IndexLeverage doesn't apply here
const getRawColumns = createSelector(
  [getGMV, getReferenceEquity, noop, getSelectedMetricType, getSelectedModelName, getPortfolioBetaInstrument, getSelectedPerformanceMetricType],
  computeColumns,
)

const getFilteredColumns = createSelector(
  [getRawColumns],
  (columns) => pick(columns, ["composition", "performance", "risk", "marginalContribution", "beta", "dynamicPredictedBeta"]),
)

const getHideColumnWith = (_, props) => props.hideColumnWith || stubFalse
const getViewColumnsMap = createSelector(
  [getFilteredColumns, getMissingDataFiles, getModelId, getHideColumnWith, getPortfolioBetaInstrument],
  (columns, missingDataFiles, modelId, hideColumnWith, betaInstrument) => mapValues({
    summary: [
      ...columns.composition,
      ...columns.performance,
      ...columns.risk,
      ...betaColumnFilter(columns.beta, missingDataFiles),
      // Only display dynamic predicted beta if we have a beta instrument
      ...(betaInstrument ? [{ ...columns.dynamicPredictedBeta }] : []),
    ],
    performance: [
      ...columns.composition,
      ...columns.performance,
    ],
    exposure: [
      ...columns.composition,
    ],
    beta: [
      ...columns.composition,
      ...betaColumnFilter(columns.beta, missingDataFiles, modelId),
      // Only display dynamic predicted beta if we have a beta instrument
      ...(betaInstrument ? [{ ...columns.dynamicPredictedBeta, tooltipContents: formatBetaInstrumentTooltip(betaInstrument) }] : []),
    ],
    risk: [
      ...columns.composition,
    ],
    sensitivity: [
      ...columns.composition,
    ],
    marginal: [
      ...columns.composition,
      ...columns.marginalContribution,
    ],
  }, (columns) => filter(columns, column => !hideColumnWith(column))),
)

export const getColumns = createSelector(
  [getViewColumnsMap, getRawSort, getSelectedView, getSelectedSubView, getFactorsColumns, getIsOpPro, getDisableColumnClick, getSelectedViewIsContentSet, getCategories, getSelectedMetricType],
  makeComputeAssetAnalyticsColumns(Creators.itemClick),
)
